import { isValidEmail } from '@utils/validation';
import { Loader2, Mail, X } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useInviteUserToOrganization } from '@/hooks/useOrganizationInvitations';

import { AssignedRolesSection, type PendingChanges } from './AssignedRolesSection';

interface InviteUserDialogProps {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  organizationId,
  open,
  onOpenChange,
}) => {
  const inviteMutation = useInviteUserToOrganization();
  const [pendingChanges, setPendingChanges] = useState<PendingChanges>({
    addOrgRoles: new Set(),
    removeOrgRoles: new Set(),
    addProgramRoles: new Map(),
    removeProgramRoles: new Map(),
  });
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset all state to initial values
  const resetState = () => {
    setEmails([]);
    setInputValue('');
    setEmailError(null);
    setPendingChanges({
      addOrgRoles: new Set(),
      removeOrgRoles: new Set(),
      addProgramRoles: new Map(),
      removeProgramRoles: new Map(),
    });
  };

  const handleRoleChanges = (changes: PendingChanges) => {
    setPendingChanges(changes);
  };

  const addEmails = useCallback(
    (raw: string) => {
      // Split by comma, semicolon, space, or newline
      const parts = raw.split(/[,;\s]+/).filter(Boolean);
      const valid: string[] = [];
      const invalid: string[] = [];

      for (const part of parts) {
        const trimmed = part.trim().toLowerCase();
        if (!trimmed) continue;
        if (isValidEmail(trimmed)) {
          if (!emails.includes(trimmed) && !valid.includes(trimmed)) {
            valid.push(trimmed);
          }
        } else {
          invalid.push(trimmed);
        }
      }

      if (valid.length > 0) {
        setEmails(prev => [...prev, ...valid]);
      }

      if (invalid.length > 0) {
        setEmailError(`Invalid: ${invalid.join(', ')}`);
      } else {
        setEmailError(null);
      }

      return invalid.length === 0;
    },
    [emails],
  );

  const removeEmail = useCallback((emailToRemove: string) => {
    setEmails(prev => prev.filter(e => e !== emailToRemove));
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      if (inputValue.trim()) {
        addEmails(inputValue);
        setInputValue('');
      }
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      removeEmail(emails[emails.length - 1]);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    addEmails(pasted);
    setInputValue('');
  };

  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmails(inputValue);
      setInputValue('');
    }
  };

  const onSubmit = async () => {
    // Add any remaining input value
    let allEmails = [...emails];
    if (inputValue.trim()) {
      const trimmed = inputValue.trim().toLowerCase();
      if (isValidEmail(trimmed) && !allEmails.includes(trimmed)) {
        allEmails = [...allEmails, trimmed];
      } else if (!isValidEmail(trimmed)) {
        setEmailError(`Invalid email: ${trimmed}`);
        return;
      }
    }

    if (allEmails.length === 0) {
      setEmailError('Please enter at least one email address');
      return;
    }

    // Check if at least one role is assigned
    const hasOrgRoles = pendingChanges.addOrgRoles.size > 0;
    const hasProgramRoles = pendingChanges.addProgramRoles.size > 0;

    if (!hasOrgRoles && !hasProgramRoles) {
      toast.error('Please assign at least one role before sending the invitation');
      return;
    }

    // Build shared role payload
    let organizationRoleId: string | undefined;
    if (hasOrgRoles) {
      organizationRoleId = Array.from(pendingChanges.addOrgRoles)[0];
    }

    let programRoleIds: { [key: string]: string } | undefined;
    if (hasProgramRoles) {
      const ids: { [key: string]: string } = {};
      pendingChanges.addProgramRoles.forEach((roleIds, programId) => {
        const roleId = Array.from(roleIds)[0];
        if (roleId) {
          ids[programId] = roleId;
        }
      });
      programRoleIds = ids;
    }

    setIsSending(true);
    const succeeded: string[] = [];
    const failed: { email: string; error: string }[] = [];

    for (const email of allEmails) {
      try {
        const payload: {
          organization_id: string;
          email: string;
          organization_role_id?: string;
          program_role_ids?: { [key: string]: string };
        } = {
          organization_id: organizationId,
          email,
        };
        if (organizationRoleId) payload.organization_role_id = organizationRoleId;
        if (programRoleIds) payload.program_role_ids = programRoleIds;

        await inviteMutation.mutateAsync(payload);
        succeeded.push(email);
      } catch (error: any) {
        failed.push({
          email,
          error: error?.response?.data?.message || error?.message || 'Failed to send invitation',
        });
      }
    }

    setIsSending(false);

    if (succeeded.length > 0) {
      toast.success(
        succeeded.length === 1
          ? `Invitation sent to ${succeeded[0]}`
          : `Invitations sent to ${succeeded.length} users`,
      );
    }

    if (failed.length > 0) {
      for (const f of failed) {
        toast.error(`${f.email}: ${f.error}`);
      }
      // Keep failed emails in the list so user can retry
      setEmails(failed.map(f => f.email));
      setInputValue('');
    } else {
      resetState();
      onOpenChange(false);
    }
  };

  // Reset state when dialog closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetState();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invite Users</DialogTitle>
          <DialogDescription>
            Send invitation emails to add new members to your organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label>Email Addresses</Label>
            <div
              className="flex flex-wrap gap-1.5 rounded-md border border-input bg-background px-3 py-2 min-h-[40px] cursor-text"
              onClick={() => inputRef.current?.focus()}
            >
              {emails.map(email => (
                <Badge key={email} variant="secondary" className="gap-1 pr-1">
                  {email}
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      removeEmail(email);
                    }}
                    className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
              <div className="relative flex-1 min-w-[200px] flex items-center">
                <Mail className="absolute left-0 h-4 w-4 text-muted-foreground pointer-events-none" />
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={e => {
                    setInputValue(e.target.value);
                    setEmailError(null);
                  }}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  onBlur={handleBlur}
                  placeholder={emails.length === 0 ? 'Enter email addresses...' : ''}
                  className="border-0 shadow-none focus-visible:ring-0 pl-6 h-7 p-0"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Press Enter, comma, or semicolon to add. Paste multiple emails at once.
            </p>
            {emailError && <p className="text-sm text-destructive">{emailError}</p>}
          </div>

          {/* Use AssignedRolesSection for role assignment */}
          <AssignedRolesSection
            organizationId={organizationId}
            userRoles={[]} // Empty array since this is a new invitation
            onChangeRoles={handleRoleChanges}
            maxHeight="max-h-[300px]"
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isSending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={onSubmit}
              disabled={
                isSending ||
                (emails.length === 0 && !inputValue.trim()) ||
                (pendingChanges.addOrgRoles.size === 0 && pendingChanges.addProgramRoles.size === 0)
              }
            >
              {isSending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {emails.length <= 1 ? 'Send Invitation' : `Send ${emails.length} Invitations`}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};
